import { useMemo, useState } from "react";
import DataGrid from "react-data-grid";
import { useSelector } from "react-redux";
import AutoSizer from "react-virtualized-auto-sizer";

import { Error } from "@material-ui/icons";
import classnames from "classnames";
import {
  EvaChart,
  WELL_STATUS_CURRENT_TITLE,
  WellStatusTimeScale
} from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import {
  orderSeriesByLegend,
  resolveWellStatusSelection,
  wellStatusOverTimeChartTitle
} from "entities/charts/eva/requests/wellStatus";
import { DEFAULT_WELL_STATUS_CURRENT_REQUEST } from "entities/charts/eva/requests/wellStatusCurrent";
import { defaultWellStatusOverTimeRequest } from "entities/charts/eva/requests/wellStatusOverTime";
import { RootState } from "store/rootReducer";
import styled from "styled-components/macro";
import { WellStatusChartRequestType } from "types";
import formatPropertyName from "utils/column/formatPropertyName";
import {
  applyFieldNameMode,
  resolveSimplifyCustomFieldNames
} from "utils/column/getColumnLabelWithContext";
import { ExportFormat, exportFile } from "utils/exportUtils";

import { useChartEntities } from "hooks/charts";
import { useWellStatusLegendOrder } from "hooks/charts/useWellStatusLegendOrder";
import useColumnsDictionary from "hooks/useColumnsDictionary";
import useUser from "hooks/useUser";

import useExportWellStatusMutation from "api/useExportWellStatusMutation";

import {
  getWellStatusCurrentDataTable,
  getWellStatusOverTimeDataTable
} from "components/charts/helpers/getWellStatusDataTable";

import { ChartDataTableContextMenu } from "../toggles/data-table/ChartDataTableContextMenu";
import { CopyTableButton } from "../toggles/data-table/CopyTableButton";
import { ExportButton } from "../toggles/data-table/ExportButton";
import { useChartDataTableInteractions } from "../toggles/data-table/hooks/useChartDataTableInteractions";
import { createChartDataTableHeaderRenderer } from "../toggles/data-table/utils/createChartDataTableHeaderRenderer";

const MIN_COLUMN_WIDTH = 150;

/**
 * Raw data table for both Well Status charts. A focused
 * sibling of RigFracDataTable: getWellStatusDataTable supplies the table shape and
 * useExportWellStatusMutation (the /api/v1/wellStatusChart/export route) the
 * inline export. Overlays the chart when features.datatable.active.
 *
 * Copy support (cell click, right-click cell/row/column, copy-whole-table) reuses
 * the same shared generic hooks/components as the wells and rig/frac tables.
 */
function WellStatusDataTable() {
  const { id, entityKind } = useChartsContext();
  const columnsDictionary = useColumnsDictionary(entityKind);
  const { apiResponse, request, chartType, chartFocus, screenshot } =
    useChartEntities(id);
  const { isReadonly, hasTrialAccount } = useUser();
  const [exportError, setExportError] = useState<Maybe<string>>();

  const isCurrent = chartType === EvaChart.WellStatusCurrent;

  const selections = request as unknown as Maybe<{
    timeScale?: WellStatusTimeScale;
    allStatuses?: boolean;
    customRange?: { from?: string; to?: string };
  }>;
  const timeScale = selections?.timeScale;
  const customRange = selections?.customRange;
  const isAllStatuses = selections?.allStatuses === true;

  // The group dimension's HUMAN name ("Resource Play"): the server only knows the
  // raw property, so resolve it the same way useWellStatusChartOptions does.
  const globalGroupBy = useSelector((state: RootState) => state.groupBy.globalGroupBy);
  // EVA-127 FIX 4: the chart-focus group-by is the SANITIZED object — the feature
  // allow-list in DEFAULT_CHART_FOCUS_ENTITY.properties.groupBy omits subgroup,
  // defaultUnit and metadata, the three fields applyFieldNameMode rebuilds the
  // label from, so a restored dashboard would hand it nothing to re-add. Resolve
  // the real column from columnsDictionary by property, exactly as both
  // useChartLegendTitle hooks do, and fall back to the sanitized object on a
  // miss. useColumnsDictionary also returns `{}` on first render (it populates
  // in an effect), so the fallback is what paints first — benign, the effect
  // re-runs once columns load.
  const activeGroupBy = chartFocus?.active
    ? (chartFocus?.properties?.groupBy?.property &&
        columnsDictionary?.[
          formatPropertyName(chartFocus.properties.groupBy.property)
        ]) ||
      chartFocus?.properties?.groupBy
    : globalGroupBy;
  // EVA-127: route through the shared field-name interpreter — see the
  // identical fix in useWellStatusChartOptions.ts. Without this the group
  // column header came straight from the stored title, losing the subgroup
  // and unit for a custom field.
  const simplifyCustomFieldNames = resolveSimplifyCustomFieldNames(
    !!screenshot?.active,
    screenshot?.properties?.preset
  );
  const focusTitle = activeGroupBy?.title
    ? (applyFieldNameMode(
        activeGroupBy.title,
        activeGroupBy,
        simplifyCustomFieldNames
      ) as Maybe<string>)
    : undefined;
  const legendOrder = useWellStatusLegendOrder();

  const { columns, rows } = useMemo(() => {
    if (!apiResponse) return { columns: [], rows: [] };
    // Columns follow the series, so order the series by the legend first — same
    // reordering the chart applies, so the table's headers read left-to-right in
    // the order the legend lists them.
    const ordered = {
      ...apiResponse,
      series: orderSeriesByLegend(apiResponse.series ?? [], legendOrder)
    };
    return isCurrent
      ? getWellStatusCurrentDataTable(ordered, isAllStatuses, focusTitle)
      : getWellStatusOverTimeDataTable(ordered, timeScale, customRange);
  }, [
    apiResponse,
    isCurrent,
    isAllStatuses,
    focusTitle,
    timeScale,
    customRange,
    legendOrder
  ]);

  const {
    highlightedCell,
    contextMenu,
    activeColumnCopyAlerts,
    handleCellClick,
    handleContextMenuAction,
    copyColumnData,
    copyTableData,
    setContextMenu
  } = useChartDataTableInteractions({ rows, columns, hasTrialAccount, isReadonly });

  const handleHeaderContextMenu = (
    event: React.MouseEvent,
    columnName: string,
    columnKey: string
  ) => {
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      row: {},
      columnKey,
      columnName
    });
  };

  // Same caption the chart carries, so the table and the chart agree on what is
  // being shown (including the granularity).
  const title = isCurrent
    ? apiResponse?.title ?? WELL_STATUS_CURRENT_TITLE
    : wellStatusOverTimeChartTitle(timeScale, customRange);
  // Data OUT of the product — export and copy-to-clipboard — is not available to
  // trial or read-only accounts. Same rule and same shape as the wells
  // ChartDataTable, which hides the whole actions row rather than rendering
  // disabled buttons: an Export button a trial user can press and watch fail is
  // worse than no button.
  const canExport = !isReadonly && !hasTrialAccount;

  const exportMutation = useExportWellStatusMutation(
    (response, params) => {
      setExportError(null);
      exportFile(response.data, params.filename);
    },
    // Surface the mutation's specific message when it has one ("No data to
    // export" for the JSON-bodied no-data response) over the generic fallback.
    (error) => setExportError(error?.message || "Failed to export chart data")
  );

  const handleExport = (format: ExportFormat) => {
    // The entity request holds the lean well-status request (set by the
    // controller); default it — per the ACTIVE chart type — for an export before
    // the first response lands. chartType is FORCED from the live chart type: the
    // persisted request's chartType lags a chart-type switch (it is only
    // rewritten when the next response lands), so trusting it could export the
    // previous chart's table shape.
    const base = isCurrent
      ? DEFAULT_WELL_STATUS_CURRENT_REQUEST
      : defaultWellStatusOverTimeRequest();
    const selections = request as unknown as Maybe<{
      status?: string;
      allStatuses?: boolean;
      includeDrainLegs?: boolean;
    }>;
    // The same lag that forces chartType applies to the SELECTION: the two charts
    // have disjoint status vocabularies, so exporting mid-switch could otherwise
    // post "SPUD" to the current-status endpoint — which matches nothing and
    // returns zero rows without erroring on any layer.
    //
    // Resolved EXACTLY as useWellStatusController does (validate, then blank the
    // status when the flag is set), so the exported file always describes the
    // same query the chart drew.
    const allStatuses = isCurrent && selections?.allStatuses === true;
    const status = resolveWellStatusSelection(selections?.status, isCurrent);
    const leanRequest = {
      ...base,
      ...(request as unknown as Maybe<Partial<WellStatusChartRequestType>>),
      chartType: isCurrent ? "WellStatusCurrent" : "WellStatusOverTime",
      status: allStatuses ? "" : status,
      allStatuses,
      // Same lag as chartType/status above: the over-time toggle can persist
      // `includeDrainLegs: false` on the shared entity request, which then
      // outlives a switch to the current-status chart. That chart has no
      // toggle and must always pin the flag true (see wellStatusCurrent.ts),
      // exactly as useWellStatusController does, so an export can never carry
      // a stale `false`.
      includeDrainLegs: isCurrent
        ? DEFAULT_WELL_STATUS_CURRENT_REQUEST.includeDrainLegs
        : selections?.includeDrainLegs ??
          defaultWellStatusOverTimeRequest().includeDrainLegs
    } as WellStatusChartRequestType;
    exportMutation.mutate({
      request: leanRequest,
      format,
      filename: `EVA ${title}.${format}`
    });
  };

  return (
    <Wrapper data-testid="well-status-data-table">
      <ChartTitle>{title}</ChartTitle>
      <DataTableWrapper>
        <div style={{ height: "100%" }}>
          <AutoSizer>
            {({ width, height }) => (
              <StyledGrid
                className={classnames("rdg-light")}
                columns={columns.map((column) => ({
                  ...column,
                  resizable: true,
                  minWidth: MIN_COLUMN_WIDTH,
                  width: Math.max(
                    MIN_COLUMN_WIDTH,
                    Number(width) / (columns.length || 1)
                  ),
                  headerRenderer: createChartDataTableHeaderRenderer({
                    hasTrialAccount,
                    isReadonly,
                    activeColumnCopyAlerts,
                    onColumnCopy: copyColumnData,
                    onHeaderContextMenu: handleHeaderContextMenu
                  }),
                  formatter: (props) => {
                    const cellValue = props.row[props.column.key];
                    return (
                      <div
                        style={{ width: "100%", height: "100%" }}
                        onContextMenu={(e) => {
                          if (hasTrialAccount || isReadonly) return;
                          e.preventDefault();
                          e.stopPropagation();
                          setContextMenu({
                            visible: true,
                            x: e.clientX,
                            y: e.clientY,
                            row: props.row,
                            cellValue,
                            columnKey: props.column.key
                          });
                        }}>
                        {cellValue}
                      </div>
                    );
                  },
                  cellClass: (_row: Record<string, unknown>, rowIdx: number) =>
                    highlightedCell &&
                    highlightedCell.rowIndex === rowIdx &&
                    highlightedCell.columnKey === column.key
                      ? "cell-highlighted"
                      : undefined
                }))}
                rows={rows}
                onRowClick={handleCellClick}
                style={{ width: `${Number(width)}px`, height: `${Number(height)}px` }}
              />
            )}
          </AutoSizer>
        </div>
      </DataTableWrapper>
      {canExport && (
        <DataTableActionsWrapper>
          {exportError && (
            <ErrorContainer>
              <Error style={{ top: 3 }} /> {exportError}
            </ErrorContainer>
          )}
          <ExportButton exporting={exportMutation.isLoading} onExport={handleExport} />
          <CopyTableButton onCopy={copyTableData} isDisabled={!canExport} />
        </DataTableActionsWrapper>
      )}

      {contextMenu && (
        <ChartDataTableContextMenu
          contextMenu={contextMenu}
          onAction={handleContextMenuAction}
        />
      )}
    </Wrapper>
  );
}

export default WellStatusDataTable;

const Wrapper = styled.div`
  position: absolute;
  display: flex;
  gap: 5px;
  flex-direction: column;
  width: 100%;
  height: 100%;
  z-index: 9;
  background-color: #fff;
  padding: 10px;
`;

const ChartTitle = styled.div`
  flex-grow: 0;
  text-transform: uppercase;
  font-weight: 600;
`;

const DataTableWrapper = styled.div`
  flex-grow: 1;
  overflow-x: auto;
`;

const DataTableActionsWrapper = styled.div`
  flex-grow: 0;
  display: flex;
  align-items: center;
  text-align: right;
  justify-content: flex-end;
`;

// Cell hover + copy-highlight pulse, mirroring the wells ChartDataTable.
const StyledGrid = styled(DataGrid)`
  .rdg-cell {
    &:hover {
      background-color: rgba(220, 233, 246, 1);
    }
  }

  .cell-highlighted {
    background-color: #e6fffb !important;
    animation: cellCopyPulse 1s ease-out;
  }

  @keyframes cellCopyPulse {
    0% {
      background-color: #52c41a;
    }
    50% {
      background-color: #b7eb8f;
    }
    100% {
      background-color: #e6fffb;
    }
  }
`;

const ErrorContainer = styled.div`
  color: var(--color-danger);
  font-weight: var(--fontWeightMedium);
`;
